'use client';

import * as React from 'react';
type SessionStatus = 'loading' | 'authenticated' | 'unauthenticated';

export type RuntimeConfig = {
  capabilities: {
    transcriptionAdmin: boolean;
  };
  debug?: {
    namespaces: string[];
  };
  diagram?: {
    krokiBaseUrl?: string;
  };
  collab?: {
    wsUrl: string | null;
  };
};

type CachedRuntimeConfig = {
  value: RuntimeConfig;
  fetchedAt: number;
};

const RUNTIME_CONFIG_TTL_MS = 60_000;
let runtimeConfigCache: CachedRuntimeConfig | null = null;
let runtimeConfigPromise: Promise<RuntimeConfig> | null = null;

async function fetchRuntimeConfig(): Promise<RuntimeConfig> {
  if (runtimeConfigCache && Date.now() - runtimeConfigCache.fetchedAt < RUNTIME_CONFIG_TTL_MS) {
    return runtimeConfigCache.value;
  }

  if (runtimeConfigPromise) {
    return runtimeConfigPromise;
  }

  runtimeConfigPromise = (async () => {
    const response = await fetch('/api/config', {
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to load runtime config (status ${response.status})`);
    }

    const data = (await response.json()) as RuntimeConfig;
    runtimeConfigCache = { value: data, fetchedAt: Date.now() };
    return data;
  })();

  try {
    return await runtimeConfigPromise;
  } finally {
    runtimeConfigPromise = null;
  }
}

export function useRuntimeConfig(sessionStatus: SessionStatus) {
  const [config, setConfig] = React.useState<RuntimeConfig | null>(null);
  const [error, setError] = React.useState<Error | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);

  React.useEffect(() => {
    if (sessionStatus === 'loading') {
      return;
    }

    let active = true;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await fetchRuntimeConfig();
        if (active) {
          setConfig(data);
        }
      } catch (err) {
        if (active) {
          setError(err instanceof Error ? err : new Error('Failed to load runtime config'));
          setConfig(null);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      active = false;
    };
  }, [sessionStatus]);

  return React.useMemo(
    () => ({
      config,
      error,
      loading: sessionStatus === 'loading' || loading,
    }),
    [config, error, loading, sessionStatus],
  );
}
